/**
 * This module provides a pair of functions, extractChildren and mergeChildren, which perform one level of
 * data normalization.
 *
 * Given the hierarchy of A has B children, which has C children, fetchA(5) would return data about an A,
 * which has a list of B children. extractChildren can replace the B children on A with a list of ids, and
 * merge the B children into a separate B map. mergeChildren performs the inverse operation--replacing the
 * list of ids on a normalized A with the associated children from a B map.
 */
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as logging from 'util/Logging';

/*
 * Given an object with potentially multiple lists of children, strip the children into separate objects,
 * replacing them with a list of ids instead.
 *
 *    obj: an object whose keys are a superset of childMap's keys.
 *    childMap: an object mapping keys to objects.
 *
 *    Returns: a two-element list of obj and children
 *      obj: an object with the same keys as the original argument, but with the children replaced with
 *           lists of ids.
 *      children: an object mapping the same keys as childMap to the same maps updated with the new
 *           children.
 *
 * For example:
 *
 * > obj = {name: 'John', cats: [{id: 1, name: 'Mr. Wiggles'}]}
 * > childMap={cats: {2: {id: 2, name: 'Meow'}}}
 * > extractChildren(obj, childMap)
 *   [
 *     {name: 'John', cats: [1]},
 *     {
 *       cats: {
 *         1: {id: 1, name: 'Mr. Wiggles'},
 *         2: {id: 2, name: 'Meow'},
 *       }
 *     }
 *   ]
 */
export function extractChildren(obj, childMap) {
  const parent = {
    ...obj,
  };
  const children = {};
  for (const key of Object.keys(childMap)) {
    assert(parent[key] != null, `extractChildren did not find child array at "${key}"`);
    const newChildren = parent[key].map((child) => ({
      ...child,
    }));
    children[key] = reactUpdate(childMap[key], {$merge: _.indexBy(newChildren, 'id')});
    // eslint-disable-next-line no-restricted-syntax
    parent[key] = _.pluck(newChildren, 'id');
  }

  return [parent, children];
}

/*
 * Given an object with possibly multiple lists of child ids, replace each list of ids with a list of
 * children pulled from the corresponding entry in childMap.
 *
 *    obj: an object whose keys are a superset of childMap's keys.
 *    childMap: an object mapping keys to objects.
 *
 *    Returns: an object with the same keys as obj.
 *
 * For example:
 *
 * > obj = {name: 'John', cats: [1]}
 * > childMap = {cats: {1: {id: 1, name: 'Mr. Wiggles'}}}
 * > mergeChildren(obj, childMap)
 *   {
 *     name: 'John',
 *     cats: [{id: 1, name: 'Mr. Wiggles'}],
 *   }
 */
export function mergeChildren(obj, childMap) {
  return _.mapObject(obj, (val, key) => {
    // eslint-disable-next-line benchlint/no-in-operator
    if (key in childMap) {
      return val
        .map((childId) => {
          if (childMap[key][childId] == null) {
            logging.warn(`${childId} not present in ${key} childMap: ${childMap[key]}`);
          }
          return childMap[key][childId];
        })
        .filter((child) => child != null);
    }
    return val;
  });
}
